/** @jsxImportSource @emotion/react */
import React, { useState } from 'react'
import { css } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'
import { sanitizeHtmlUrls } from 'src/utils/stringUtils'

/**
 * @interface RichTextRendererProps
 * @property {string} content - The HTML string to render
 * @property {number} [truncateLength] - The character count at which to truncate the content (default: 300)
 * @property {boolean} [showReadMore] - A flag to control the "Read More/Less" functionality (default: true)
 */
interface RichTextRendererProps {
  content: string
  truncateLength?: number
  showReadMore?: boolean
}

/**
 * RichTextRenderer component - renders sanitized HTML content with consistent styling
 *
 * This component provides a safe and consistent way to display rich HTML content
 * across the application. It includes:
 * - HTML sanitization to prevent XSS attacks
 * - Consistent styling for all rich text elements
 * - Optional content truncation with "Read More/Less" functionality
 * - Support for text alignment classes from Quill editor
 *
 * The component applies comprehensive styling for headers, paragraphs, lists,
 * links, blockquotes, and other rich text elements while maintaining security
 * through URL sanitization.
 *
 * @param props - Component props as defined in RichTextRendererProps
 * @returns JSX element representing the rendered rich text content
 */
const RichTextRenderer: React.FC<RichTextRendererProps> = ({
  content,
  truncateLength = 300,
  showReadMore = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)

  // Don't show anything if content is empty
  if (!content?.trim()) {
    return null
  }

  const shouldTruncate = showReadMore && content.length > truncateLength
  const displayContent =
    shouldTruncate && !isExpanded
      ? content.substring(0, truncateLength) + '...'
      : content

  return (
    <div>
      <div
        className='rich-text-content'
        css={css`
          color: ${colors.NEUTRAL_0};
          font-family: inherit;
          line-height: 1.5;
          overflow-wrap: break-word;
          word-break: break-word;
          hyphens: auto;

          /* Style the rich text content */
          h1,
          h2,
          h3 {
            font-weight: 600;
            color: ${colors.NEUTRAL_0};
            margin: 12px 0 8px 0;
          }

          h1 {
            font-size: 1.75em;
          }
          h2 {
            font-size: 1.5em;
          }
          h3 {
            font-size: 1.25em;
          }

          p {
            color: ${colors.NEUTRAL_0};
            margin: 8px 0;
          }

          /* Force list styles - using pseudo-elements as workaround for global reset */
          &.rich-text-content ul {
            margin: 8px 0 !important;
            padding-left: 0 !important;
            color: ${colors.NEUTRAL_0} !important;
            list-style: none !important;
          }

          &.rich-text-content ol {
            margin: 8px 0 !important;
            padding-left: 0 !important;
            color: ${colors.NEUTRAL_0} !important;
            list-style: none !important;
            counter-reset: list-counter !important;
          }

          &.rich-text-content ul li {
            margin: 4px 0 !important;
            padding-left: 28px !important;
            color: ${colors.NEUTRAL_0} !important;
            display: block !important;
            position: relative !important;
          }

          &.rich-text-content ol li {
            margin: 4px 0 !important;
            padding-left: 28px !important;
            color: ${colors.NEUTRAL_0} !important;
            display: block !important;
            position: relative !important;
            counter-increment: list-counter !important;
          }

          /* Create custom bullet points and numbers */
          &.rich-text-content ul li::before {
            content: '•' !important;
            color: ${colors.NEUTRAL_0} !important;
            font-size: 16px !important;
            line-height: inherit !important;
            position: absolute !important;
            left: 8px !important;
            top: 50% !important;
            transform: translateY(-50%) !important;
          }

          &.rich-text-content ol li::before {
            content: counter(list-counter) '.' !important;
            color: ${colors.NEUTRAL_0} !important;
            font-weight: bold !important;
            line-height: inherit !important;
            position: absolute !important;
            left: 8px !important;
            top: 50% !important;
            transform: translateY(-50%) !important;
            min-width: 20px !important;
          }

          strong {
            font-weight: 600;
            color: ${colors.NEUTRAL_0};
          }

          em {
            font-style: italic;
            color: ${colors.NEUTRAL_0};
          }

          u {
            text-decoration: underline;
            color: ${colors.NEUTRAL_0};
          }

          s {
            text-decoration: line-through;
            color: ${colors.NEUTRAL_0};
          }

          a {
            color: ${colors.PRIMARY};
            text-decoration: underline;

            &:hover {
              opacity: 0.8;
            }
          }

          img {
            max-width: 100%;
            height: auto;
            border-radius: 4px;
          }

          blockquote {
            border-left: 4px solid ${colors.PRIMARY};
            padding-left: 16px;
            margin: 16px 0;
            font-style: italic;
            opacity: 0.9;
            color: ${colors.NEUTRAL_0};
          }

          /* Text alignment classes from Quill */
          .ql-align-left {
            text-align: left;
          }
          .ql-align-center {
            text-align: center;
          }
          .ql-align-right {
            text-align: right;
          }
          .ql-align-justify {
            text-align: justify;
          }
        `}
        dangerouslySetInnerHTML={{ __html: sanitizeHtmlUrls(displayContent) }}
      />
      {shouldTruncate && (
        <button
          css={css`
            background: none;
            border: none;
            color: ${colors.PRIMARY};
            cursor: pointer;
            padding: 8px 0;
            font-size: 14px;
            text-decoration: underline;
            &:hover {
              opacity: 0.8;
            }
          `}
          onClick={(e) => {
            e.stopPropagation()
            setIsExpanded(!isExpanded)
          }}
        >
          {isExpanded ? 'Read Less' : 'Read More'}
        </button>
      )}
    </div>
  )
}

export default RichTextRenderer
